import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import { motion } from "framer-motion";
import {
  Loader2, Rocket, Sparkles, ArrowRight, Printer, TrendingUp, CheckCircle2, Target, Award, CalendarCheck,
  Mail, Download, Send, Lock, Unlock, Euro, Linkedin, ListOrdered, BookmarkPlus, History,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import ShareDialog from "@/components/ShareDialog";
import { useAuth } from "@/context/AuthContext";
import { fetchAssessment, requestReport, reportDownloadUrl, claimAssessment } from "@/lib/apiClient";

const REVENUE_OPTIONS = [
  { key: "lt2", label: "unter 2 Mio. €" },
  { key: "2-5", label: "2–5 Mio. €" },
  { key: "5-10", label: "5–10 Mio. €" },
  { key: "10-25", label: "10–25 Mio. €" },
  { key: "gt25", label: "über 25 Mio. €" },
];
const inputCls =
  "h-12 rounded-none bg-[#111] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-brand-orange";

const ReportSection = ({ assessmentId, defaultCompany }) => {
  const [form, setForm] = useState({ name: "", email: "", revenue: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const valid = emailValid && !!form.revenue && consent;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    try {
      const res = await requestReport(assessmentId, {
        email: form.email, name: form.name, company: defaultCompany || "",
        revenue: form.revenue, consent: true,
      });
      setDone(res);
      toast.success(res.emailed ? "Lagebericht per E-Mail versendet." : "Werthebel freigeschaltet.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  const levers = done?.value_levers;

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-4 no-print" data-testid="report-section">
      {!done ? (
        <div className="bg-gradient-to-br from-brand-orange/15 to-transparent border border-brand-orange/30 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-4 w-4 text-brand-orange" />
            <h2 className="font-display text-base uppercase tracking-tight text-white">
              Ihr individueller Euro-Werthebel + PDF
            </h2>
          </div>
          <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
            Sehen Sie in Euro, wie viel Wert in Ihren schwächsten Feldern steckt – berechnet auf
            Basis Ihrer Umsatzgröße. Sie erhalten den vollständigen Lagebericht als gebrandetes PDF
            an Ihre E-Mail.
          </p>
          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="report-form">
            <Input data-testid="report-name" value={form.name} onChange={set("name")} placeholder="Name" className={inputCls} />
            <Input data-testid="report-email" type="email" value={form.email} onChange={set("email")} placeholder="ihre@firma.de" className={inputCls} />
            <select data-testid="report-revenue" value={form.revenue} onChange={set("revenue")} className={`${inputCls} px-3`}>
              <option value="">Jahresumsatz…</option>
              {REVENUE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <Button type="submit" data-testid="report-submit" disabled={sending || !valid}
              className="h-12 rounded-none bg-brand-orange text-black hover:bg-white disabled:opacity-40 font-bold uppercase tracking-wider text-sm transition-colors">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" />Freischalten</>}
            </Button>
            <label className="sm:col-span-2 lg:col-span-4 flex items-start gap-2.5 cursor-pointer mt-1">
              <Checkbox data-testid="report-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)}
                className="mt-0.5 rounded-none border-white/30 data-[state=checked]:bg-brand-orange data-[state=checked]:border-brand-orange data-[state=checked]:text-black" />
              <span className="text-xs text-white/55 leading-relaxed">
                Ich willige ein, dass feNANCE meine Angaben zur Zusendung des Lageberichts und zur
                Kontaktaufnahme verarbeitet (Widerruf jederzeit an fe@fenance.de). Es gelten die{" "}
                <Link to="/datenschutz" className="text-brand-orange underline">Datenschutzhinweise</Link>.
              </span>
            </label>
          </form>
        </div>
      ) : (
        <div className="border border-brand-orange/40 bg-brand-orange/5 p-6 sm:p-8" data-testid="report-result">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
            <div className="flex items-center gap-2">
              <Unlock className="h-5 w-5 text-brand-orange" />
              <h2 className="font-display text-base uppercase tracking-tight text-white">
                Ihr Euro-Werthebel {levers?.revenue_label ? `· ${levers.revenue_label}` : ""}
              </h2>
            </div>
            <a href={reportDownloadUrl(assessmentId)} target="_blank" rel="noopener noreferrer" data-testid="report-download"
              className="inline-flex items-center gap-2 text-brand-orange hover:text-white text-sm uppercase tracking-wider font-semibold">
              <Download className="h-4 w-4" /> PDF öffnen
            </a>
          </div>
          {levers?.items?.length ? (
            <div className="grid md:grid-cols-3 gap-4">
              {levers.items.map((it) => (
                <div key={it.title} className="bg-card border border-white/10 p-5">
                  <Euro className="h-6 w-6 text-brand-orange" />
                  <h3 className="mt-3 font-display text-sm uppercase tracking-tight text-white">{it.title}</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">{it.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/70">Ihre Felder sind bereits stark – kein akuter Euro-Hebel identifiziert. Sehr gut!</p>
          )}
          {levers?.headline && (
            <p className="mt-5 text-sm text-white font-semibold border-l-2 border-brand-orange pl-4">{levers.headline}</p>
          )}
          <p className="mt-3 text-xs text-white/40">{levers?.note}</p>
          <p className="mt-4 text-sm text-white/70">{done.message}</p>
        </div>
      )}
    </section>
  );
};



const zoneColor = (z) => ({ 1: "#ef4444", 2: "#f18e00", 3: "#22c55e" }[z]);

const ZOHO_BOOKING = "https://fe-fenance.zohobookings.eu/#/239609000000041076";

const shortName = (name) =>
  ({
    "Transparenz schaffen": "Transparenz",
    "Finanzstrategie festlegen": "Strategie",
    "Finanzen planen": "Planung",
    "Finanzierung optimieren": "Finanzierung",
    "Reporting standardisieren": "Reporting",
    "Profitabilität steigern": "Profitabilität",
    "Nachfolgefähigkeit sicherstellen": "Nachfolge",
  }[name] || name);

const RadarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-black border border-white/20 px-3 py-2 text-xs">
      <p className="text-white font-semibold mb-1">{d.fullName}</p>
      <p className="text-brand-orange">Ihre Note: {d.note} (Zone {d.zone})</p>
      <p className="text-white/50">Benchmark: Note {d.benchmarkNote}</p>
    </div>
  );
};

export default function ResultsPage() {
  const { id } = useParams();
  const location = useLocation();
  const { isAuthed } = useAuth() || {};
  const [data, setData] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [shareOpen, setShareOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const claimedRef = useRef(false);

  useEffect(() => {
    if (!data) {
      fetchAssessment(id)
        .then(setData)
        .catch(() => toast.error("Auswertung nicht gefunden."))
        .finally(() => setLoading(false));
    }
  }, [id, data]);

  // KI läuft im Hintergrund – so lange pollen, bis sie fertig ist.
  useEffect(() => {
    if (!data || data.ai_status !== "pending") return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const fresh = await fetchAssessment(id);
        setData(fresh);
        if (fresh.ai_status === "ready" || tries > 25) clearInterval(iv);
      } catch (e) { console.debug("KI-Status-Polling, erneuter Versuch:", e?.message); }
    }, 4000);
    return () => clearInterval(iv);
  }, [id, data?.ai_status]);

  // Eingeloggt? Check automatisch dem Konto zuordnen.
  useEffect(() => {
    if (isAuthed && id && !claimedRef.current) {
      claimedRef.current = true;
      claimAssessment(id).then(() => setSaved(true)).catch(() => {});
    }
  }, [isAuthed, id]);

  const radarData = useMemo(() => {
    if (!data) return [];
    return data.fields.map((f) => ({
      subject: shortName(f.name),
      fullName: f.name,
      note: f.note,
      zone: f.zone,
      score: f.score,
      benchmark: data.benchmark_score,
      benchmarkNote: data.benchmark_note,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-white/60">Auswertung nicht gefunden.</p>
        <Link to="/" className="text-brand-orange underline">Zur Startseite</Link>
      </div>
    );
  }

  const oz = data.overall_zone_meta;
  const sortedFields = [...data.fields].sort((a, b) => b.note - a.note);
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://fenance.de";
  const linkedInPost =
    `Ich habe gerade den feNANCE Tragfähigkeits-Check gemacht – eine ehrliche ` +
    `Standortbestimmung für die Finanzorganisation im Mittelstand.\n\n` +
    `Mein Ergebnis: ${oz.name} (Gesamtnote ${data.overall_note.toFixed(1).replace(".", ",")} von 6).\n\n` +
    `Die meisten Inhaber führen. Wenige steuern. Der Check zeigt über 7 Handlungsfelder hinweg, ` +
    `wo im Unternehmen Wert liegt – und wie man vom Blindflug zum Instrumentenflug kommt.\n\n` +
    `Mach den Check selbst (kostenlos, ~5 Min.):\n\n` +
    `#Mittelstand #CFO #Unternehmenswert #Finanzen #feNANCE`;

  return (
    <div className="min-h-screen relative z-10" data-testid="results-page">
      <BrandHeader minimal />
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        url={shareUrl}
        title="feNANCE Tragfähigkeits-Check"
        defaultText={linkedInPost}
      />

      {/* Print-only branded header */}
      <div className="print-only px-10 pt-2 pb-4 flex items-center justify-between border-b border-white/20">
        <img src="/logo-white-orange.png" alt="feNANCE" className="h-14 w-auto" />
        <div className="text-right">
          <div className="font-display text-white text-sm tracking-tight">TRAGFÄHIGKEITS-CHECK · LAGEBERICHT</div>
          <div className="text-xs text-white/60 mt-1">
            {data.contact?.company ? `${data.contact.company} · ` : ""}
            {new Date(data.created_at).toLocaleDateString("de-DE")}
          </div>
        </div>
      </div>

      {/* HERO SUMMARY */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-40" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-14">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
                {data.contact?.company ? `Ihr Lagebericht · ${data.contact.company}` : "feNANCE · Mission Control"}
              </span>
              <h1 className="mt-4 font-display uppercase text-3xl sm:text-5xl tracking-tight text-white leading-tight">
                Ihr Lagebericht
              </h1>
              <div className="mt-6 flex items-center gap-4 flex-wrap">
                <div
                  className="inline-flex items-center gap-3 px-4 py-2 border"
                  style={{ borderColor: oz.color, backgroundColor: `${oz.color}1a` }}
                  data-testid="overall-zone-badge"
                >
                  <span className="font-display text-sm" style={{ color: oz.color }}>
                    {oz.label}
                  </span>
                  <span className="text-sm text-white font-semibold">{oz.name}</span>
                </div>
                <div className="text-white/60 text-sm">
                  Gesamtnote{" "}
                  <span className="text-white font-display text-lg" data-testid="overall-note">
                    {data.overall_note.toFixed(1)}
                  </span>{" "}
                  <span className="text-white/40">/ Benchmark {data.benchmark_note.toFixed(1)}</span>
                </div>
              </div>
              <p className="mt-4 text-sm text-white/50 max-w-md">{oz.desc}</p>
            </motion.div>

            <div className="no-print flex flex-col sm:flex-row gap-3 self-start">
              <Button
                data-testid="share-result-btn"
                onClick={() => setShareOpen(true)}
                className="h-12 px-6 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-xs transition-colors"
              >
                <Linkedin className="mr-2 h-4 w-4" />
                Ergebnis teilen
              </Button>
              <Button
                data-testid="print-btn"
                onClick={() => window.print()}
                variant="outline"
                className="h-12 px-6 rounded-none border-brand-orange text-brand-orange hover:bg-brand-orange/10 bg-transparent uppercase tracking-wider text-xs font-bold"
              >
                <Printer className="mr-2 h-4 w-4" />
                Als PDF speichern
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* RADAR + AI SUMMARY */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-card border border-white/10 p-6 min-w-0" data-testid="radar-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-brand-orange" />
            <h2 className="font-display text-sm uppercase tracking-wide text-white">
              Ihr Steuerungs-Profil
            </h2>
          </div>
          <div className="h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="#333" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: "#aaa", fontSize: 11 }}
                />
                <PolarRadiusAxis
                  domain={[0, 6]}
                  tickCount={7}
                  tick={{ fill: "#555", fontSize: 9 }}
                  axisLine={false}
                />
                <defs>
                  <linearGradient id="radarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f18e00" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#f18e00" stopOpacity={0.35} />
                  </linearGradient>
                </defs>
                <Radar
                  name="Benchmark"
                  dataKey="benchmark"
                  stroke="#8a8a8a"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  fill="#ffffff"
                  fillOpacity={0}
                />
                <Radar
                  name="Ihr Ergebnis"
                  dataKey="score"
                  stroke="#f18e00"
                  strokeWidth={3}
                  fill="url(#radarFill)"
                  fillOpacity={1}
                  dot={{ r: 4, fill: "#f18e00", stroke: "#000", strokeWidth: 1 }}
                  activeDot={{ r: 6, fill: "#fff", stroke: "#f18e00", strokeWidth: 2 }}
                  isAnimationActive
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "#fff", paddingTop: 8 }}
                />
                <Tooltip content={<RadarTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-white/40 mt-2 text-center">
            Skala: außen = volle Steuerung (Note 1), innen = Blindflug (Note 6)
          </p>
        </div>

        <div className="lg:col-span-2 bg-gradient-to-b from-brand-orange/10 to-transparent border border-brand-orange/30 p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-brand-orange" />
            <h2 className="font-display text-sm uppercase tracking-wide text-white">
              Management-Summary
            </h2>
            {data.ai_status === "pending" && (
              <span
                data-testid="ai-pending-badge"
                className="ml-auto inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-wider text-brand-orange/90"
              >
                <Loader2 className="h-3 w-3 animate-spin" /> KI verfeinert…
              </span>
            )}
          </div>
          <p
            className="text-sm text-white/80 leading-relaxed flex-1"
            data-testid="ai-summary"
          >
            {data.ai_summary || "Ihre individuelle Zusammenfassung finden Sie in den Empfehlungen der einzelnen Handlungsfelder."}
          </p>
          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="flex items-start gap-2 text-xs text-white/60 leading-relaxed">
              <Target className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
              <span>{data.overall_benchmark_fact}</span>
            </div>
          </div>
        </div>
      </section>

      <ReportSection assessmentId={data.id} defaultCompany={data.contact?.company} />

      {/* SAVE / ACCOUNT CTA */}
      <section className="no-print max-w-7xl mx-auto px-6 sm:px-10 -mt-4 mb-4">
        {isAuthed ? (
          <div
            data-testid="result-saved-note"
            className="flex items-center gap-3 border border-green-500/30 bg-green-500/5 px-5 py-4"
          >
            <History className="h-5 w-5 text-green-400 shrink-0" />
            <p className="text-sm text-white/75">
              Dieser Check ist in Ihrem Konto gespeichert.{" "}
              <Link to="/mein-verlauf" className="text-brand-orange hover:underline" data-testid="result-to-dashboard">
                Verlauf ansehen
              </Link>
            </p>
          </div>
        ) : (
          <div
            data-testid="result-save-cta"
            className="flex flex-col sm:flex-row sm:items-center gap-3 border border-brand-orange/30 bg-brand-orange/5 px-5 py-4"
          >
            <BookmarkPlus className="h-5 w-5 text-brand-orange shrink-0" />
            <p className="text-sm text-white/75 flex-1">
              Legen Sie ein kostenloses Konto an, um diesen Check zu speichern und Ihre Entwicklung über die Zeit zu verfolgen.
            </p>
            <Link
              to={`/registrieren?next=/ergebnis/${data.id}`}
              data-testid="result-register-link"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-xs transition-colors whitespace-nowrap"
            >
              Check speichern <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </section>

      {/* FIELD BREAKDOWN */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-lg uppercase tracking-tight text-white">
            Handlungsfelder & nächste Schritte
          </h2>
          <span className="text-xs text-white/40">sortiert nach Priorität</span>
        </div>

        <div className="space-y-5">
          {sortedFields.map((f, i) => {
            const c = zoneColor(f.zone);
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-card border border-white/10 overflow-hidden avoid-break"
                data-testid={`result-field-${f.id}`}
              >
                <div className="grid md:grid-cols-12">
                  {/* Left: note & zone */}
                  <div
                    className="md:col-span-3 p-6 flex flex-col justify-between border-b md:border-b-0 md:border-r border-white/10"
                    style={{ backgroundColor: `${c}0d` }}
                  >
                    <div>
                      <span className="text-xs tracking-[0.15em] uppercase text-white/40">
                        HF {String(f.id).padStart(2, "0")}
                      </span>
                      <h3 className="mt-2 font-display text-base uppercase tracking-tight text-white leading-snug">
                        {f.name}
                      </h3>
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                      <span
                        className="h-12 w-12 flex items-center justify-center font-display text-xl"
                        style={{ backgroundColor: c, color: "#000" }}
                      >
                        {f.note}
                      </span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: c }}>
                          {f.zone_label}
                        </p>
                        <p className="text-xs text-white/40">Note {f.note} / 6</p>
                      </div>
                    </div>
                    <Link
                      to={`/handlungsfeld/${f.id}`}
                      data-testid={`result-playbook-${f.id}`}
                      className="no-print mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-brand-orange hover:text-white transition-colors"
                    >
                      Playbook öffnen
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  {/* Right: status, recommendation, actions */}
                  <div className="md:col-span-9 p-6">
                    <p className="text-sm text-white/70 leading-relaxed">{f.grade_text}</p>

                    {f.ai_recommendation && (
                      <div className="mt-5 border-l-2 border-brand-orange pl-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="h-3.5 w-3.5 text-brand-orange" />
                          <span className="text-xs uppercase tracking-wide text-brand-orange font-semibold">
                            Ihr nächster Schritt
                          </span>
                        </div>
                        <p className="text-sm text-white/85 leading-relaxed">
                          {f.ai_recommendation}
                        </p>
                      </div>
                    )}

                    {f.ai_steps?.length > 0 && (
                      <div className="mt-5" data-testid={`result-steps-${f.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <ListOrdered className="h-3.5 w-3.5 text-brand-orange" />
                          <span className="text-xs uppercase tracking-wide text-brand-orange font-semibold">
                            Zwischenschritte zur nächsten Note
                          </span>
                        </div>
                        <ol className="space-y-2">
                          {f.ai_steps.map((s, idx) => (
                            <li key={`${f.id}-step-${idx}`} className="flex items-start gap-3">
                              <span className="h-5 w-5 shrink-0 mt-0.5 flex items-center justify-center bg-brand-orange/15 text-brand-orange text-[0.7rem] font-display">
                                {idx + 1}
                              </span>
                              <span className="text-xs text-white/70 leading-relaxed">{s}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    <div className="mt-5 pt-4 border-t border-white/10 flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-white/40 shrink-0 mt-0.5" />
                      <span className="text-xs italic text-white/50 leading-relaxed">
                        <span className="not-italic text-brand-orange font-semibold uppercase tracking-wide text-[0.6rem] mr-1">Benchmark:</span>
                        {f.ai_benchmark || f.benchmark_fact}
                      </span>
                    </div>

                    {f.beispiel && (
                      <div className="mt-3 flex items-start gap-2" data-testid={`result-example-${f.id}`}>
                        <Award className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
                        <span className="text-xs text-white/60 leading-relaxed">
                          <span className="text-brand-orange font-semibold uppercase tracking-wide text-[0.65rem]">Aus der Praxis: </span>
                          {f.beispiel}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* SALES CLOSE */}
        <div
          className="mt-12 bg-brand-orange p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-8 avoid-break"
          data-testid="results-cta"
        >
          <div className="max-w-xl">
            <span className="text-xs tracking-[0.2em] uppercase text-black/60">
              Ihr nächster Schritt
            </span>
            <h3 className="mt-3 font-display text-xl sm:text-2xl uppercase tracking-tight text-black leading-snug">
              Sie kennen jetzt Ihre Lage. Jetzt kommt die Steuerung.
            </h3>
            <p className="mt-3 text-sm text-black/75 leading-relaxed">
              In einer kostenlosen 30-minütigen Lagebesprechung ordnen wir Ihren Lagebericht
              gemeinsam ein – ehrlich, ohne Pitch. Sie erfahren, wo der größte Hebel für Ihren
              Unternehmenswert liegt. Damit Sie fliegen, nicht treiben.
            </p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <a href={ZOHO_BOOKING} target="_blank" rel="noopener noreferrer" data-testid="book-btn">
              <Button className="h-14 px-8 rounded-none bg-black text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider text-sm transition-colors duration-300 w-full">
                <CalendarCheck className="mr-2 h-5 w-5" />
                Lagebesprechung buchen
              </Button>
            </a>
            <a
              href="mailto:fe@fenance.de"
              data-testid="mail-link"
              className="text-center text-xs text-black/70 hover:text-black underline underline-offset-4"
            >
              oder direkt schreiben: fe@fenance.de
            </a>
          </div>
        </div>

        <div className="mt-6 flex justify-center no-print">
          <Link to="/" data-testid="restart-btn">
            <Button
              variant="ghost"
              className="h-11 px-6 rounded-none text-white/50 hover:text-white hover:bg-white/5 uppercase tracking-wider text-xs"
            >
              <Rocket className="mr-2 h-4 w-4" />
              Neuen Lagecheck starten
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Print-only footer */}
        <div className="print-only mt-8 pt-4 border-t border-white/20 text-center text-xs text-white/50">
          feNANCE · CFO as-a-Service · Florian Ebert · fenance.de · fe@fenance.de — Termin buchen:
          fe-fenance.zohobookings.eu
        </div>
      </section>
      <Footer />
    </div>
  );
}
